import { Component, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import {
  UserEntityItem,
  ConfigurationService,
} from 'app/providers/semantria-api/configuration.service';
import { EvaluationService } from 'app/providers/semantria-api/evaluation-service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ConfigurationDetailsTabBase, Highlight } from '../configuration-details-tab-base';
import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';
import { TaxonomyTargetWatcherService } from '../../services/taxonomy-target-watcher.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-entities',
  templateUrl: './configuration-entities.component.html',
  styleUrls: ['./configuration-entities.component.scss'],
  standalone: false,
})
export class ConfigurationEntitiesComponent
  extends ConfigurationDetailsTabBase<UserEntityItem>
  implements OnInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  editingEntity: UserEntityItem;

  isSaving: boolean;

  entityTypes = [];

  originalType: string;

  selectedItemType: string;
  selectedItemEntityType: 'user' | 'named';

  identityAccessor = (str) => str;
  stringFilter = (value, list) => list.filter((p) => p.toLowerCase().includes(value.toLowerCase()));

  getHighlightText = (doc: DocumentAnalysis, documentText: string, itemValues: string[]) => {
    const highlights: Highlight[] = [];
    if (doc.entities) {
      doc.entities
        .filter(
          (entity) =>
            entity.mentions &&
            entity.type === 'user' &&
            itemValues.some(
              (value) => value && value.toLocaleLowerCase().includes(entity.title.toLowerCase())
            )
        )
        .reduce((prev, entity) => prev.concat(entity.mentions), [])
        .filter((mention) => mention.locations)
        .forEach((mention) => {
          mention.locations.forEach((location) => {
            const offset = location.hasOwnProperty('char_offset')
              ? location.char_offset
              : location.offset;
            const length = location.hasOwnProperty('char_length')
              ? location.char_length
              : location.length;
            const offsetType = location.hasOwnProperty('char_offset') ? 'char' : 'byte';

            highlights.push({ offset, length, offsetType });
          });
        });
    }

    super.setDocumentHitIcon(!!highlights.length, doc.id);

    return super.highlightTextStringFromHighlights(highlights, documentText);
  };

  constructor(
    protected route: ActivatedRoute,
    protected configVersionTracker: ConfigurationVersionTrackerService,
    private configService: ConfigurationService,
    private evaluationService: EvaluationService,
    private targetWatcher: TaxonomyTargetWatcherService,
    private alertService: AlertService
  ) {
    super(route, configVersionTracker);

    this.itemType = 'User Entity';

    this.loadItems = this.configService.getUserEntities.bind(this.configService);
    this.createItem = this.configService.createUserEntity.bind(this.configService);
    this.saveItem = this.configService.saveUserEntity.bind(this.configService);
    this.deleteItem = this.configService.deleteUserEntity.bind(this.configService);
    this.deleteAllItems = this.configService.deleteAllUserEntities.bind(this.configService);
    this.evaluateItem = this.evaluationService.evaluateDocuments.bind(this.evaluationService);
    this.getEvaluationConfig = this.configService.getEvaluationConfigId.bind(this.configService);
    this.getItemByClonedFrom = this.configService.getUserEntityByClonedFrom.bind(
      this.configService
    );
    this.deleteEvaluationConfig = this.configService.deleteConfiguration.bind(this.configService);
  }

  ngOnInit() {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('itemType')),
        filter((itemType) => !!itemType)
      )
      .subscribe((itemType) => (this.selectedItemType = itemType));

    this.route.queryParamMap
      .pipe(
        map((params) => params.get('entityType')),
        filter((entityType) => !!entityType)
      )
      .subscribe((entityType: 'user' | 'named') => (this.selectedItemEntityType = entityType));
  }

  onItemsLoaded($event: UserEntityItem[]) {
    super.onItemsLoaded($event);

    this.prepareAutocompleteOptions();

    if (this.selectedItemName && !this.selectedItemId) {
      const existingItem = this.itemList.find(
        (item) => item.normalized === this.selectedItemName && item.type === this.selectedItemType
      );
      if (existingItem) {
        this.editor.selectItem(existingItem, true);
      } else {
        if (this.selectedItemEntityType === 'user') {
          this.alertService.showToast(
            `The given User Entity named ${this.selectedItemName} could not be found, trying to search for it…`
          );
          this.editor.onSearchChanged(this.selectedItemName);
        } else {
          this.editor.triggerAddButtonPopover(this.selectedItemName);
        }
      }
    }
  }

  private prepareAutocompleteOptions() {
    this.entityTypes = this.itemList
      .reduce((prev, curr) => {
        if (!prev.includes(curr.type)) {
          prev.push(curr.type);
        }
        return prev;
      }, [])
      .sort();
  }

  onItemEditing($event: UserEntityItem) {
    this.editingEntity = $event;

    if ($event) {
      this.editingEntity.type = this.editingEntity.type || '';

      this.originalType = this.editingEntity.type;

      this.editor?.isEditorTemplateValid.set(!!this.editingEntity.type);
    }
  }

  onItemSaving($event) {
    this.isSaving = $event;
  }

  getItemHighlightValue(item) {
    return [item.name, item.normalized];
  }

  onItemAdded($event) {
    super.onItemAdded($event);

    const addIfMissing = (list, value) => {
      if (!list.includes(value)) {
        list.push(value);
        list.sort();
      }
    };

    addIfMissing(this.entityTypes, $event.type);

    this.targetWatcher.entityAdded($event);
  }

  onItemUpdated($event) {
    super.onItemUpdated($event);
    this.prepareAutocompleteOptions();
    this.targetWatcher.entityModified($event);
  }

  onItemsDeleted($event) {
    super.onItemsDeleted($event);
    this.targetWatcher.entitiesDeleted($event);
  }

  onAllItemsDeleted($event) {
    super.onAllItemsDeleted($event);
    this.targetWatcher.allEntitiesDeleted($event);
  }

  onPropChanged(prop, $event) {
    this.editingEntity[prop] = $event;
    this.editor.editorTemplateHasChanges.set(this.originalType !== this.editingEntity.type);

    this.editor.isEditorTemplateValid.set(!!this.editingEntity.type);
  }
}
